import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import SharedTodoNoteEditor, {
  sharedEditorSaveAdapter,
} from '../components/todoNote/SharedTodoNoteEditor';
import {
  clearStoredTodoShareSession,
  loadTodoShareByAccessToken,
  loadTodoSharePublicChrome,
  readStoredTodoShareSession,
  saveTodoShareByAccessToken,
  verifyTodoShareAccess,
} from '../services/todoShareService';
import BrandLogo from '../components/BrandLogo';

import { APP_DISPLAY_NAME } from '../appMeta';

const DEFAULT_DOCUMENT_TITLE = APP_DISPLAY_NAME;

/**
 * 공유 ToDo URL로 홈 화면 추가·PWA 설치가 되도록
 * 페이지별 Web App Manifest를 주입한다.
 * (전역 manifest.json 의 start_url="." 은 사이트 루트로 해석됨)
 */
function useSharedTodoHomeScreenManifest(publicToken: string, boardTitle: string) {
  useEffect(() => {
    if (!publicToken || typeof window === 'undefined') return;

    const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const sharePath = `${publicBase}/t/${encodeURIComponent(publicToken)}`;
    const origin = window.location.origin;
    const iconBase = `${origin}${publicBase}`;
    const label = (boardTitle || '공유 ToDo').trim() || '공유 ToDo';
    const shortLabel = label.length > 12 ? `${label.slice(0, 11)}…` : label;

    const manifest = {
      name: label,
      short_name: shortLabel,
      description: '공유 ToDo 노트',
      start_url: sharePath,
      scope: sharePath,
      display: 'standalone',
      theme_color: '#000000',
      background_color: '#ffffff',
      icons: [
        {
          src: `${iconBase}/android-chrome-192x192.png`,
          type: 'image/png',
          sizes: '192x192',
        },
        {
          src: `${iconBase}/android-chrome-512x512.png`,
          type: 'image/png',
          sizes: '512x512',
        },
        {
          src: `${iconBase}/apple-touch-icon.png`,
          type: 'image/png',
          sizes: '180x180',
        },
      ],
    };

    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
    const blobUrl = URL.createObjectURL(blob);
    let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const previousHref = link?.getAttribute('href') ?? null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    link.setAttribute('href', blobUrl);

    const previousTitle = document.title;
    document.title = APP_DISPLAY_NAME;

    let appleTitle = document.querySelector<HTMLMetaElement>(
      'meta[name="apple-mobile-web-app-title"]'
    );
    const createdAppleTitle = !appleTitle;
    if (!appleTitle) {
      appleTitle = document.createElement('meta');
      appleTitle.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appleTitle);
    }
    const previousAppleTitle = appleTitle.content;
    appleTitle.content = shortLabel;

    return () => {
      URL.revokeObjectURL(blobUrl);
      if (link) {
        if (previousHref) link.setAttribute('href', previousHref);
        else link.setAttribute('href', `${publicBase}/manifest.json` || '/manifest.json');
      }
      document.title = previousTitle || DEFAULT_DOCUMENT_TITLE;
      if (appleTitle) {
        if (createdAppleTitle) appleTitle.remove();
        else appleTitle.content = previousAppleTitle;
      }
    };
  }, [publicToken, boardTitle]);
}

function SharedTodoPublicTopNav() {
  return (
    <header className="cp-topnav cp-todo-share-public-topnav" aria-label="공유 ToDo 네비게이션">
      <div className="cp-topnav-left">
        <span className="cp-logo cp-todo-share-public-logo" title={APP_DISPLAY_NAME}>
          <BrandLogo className="cp-brand-logo--nav" />
        </span>
      </div>
      <div className="cp-topnav-right" />
    </header>
  );
}

const SharedTodoNotePublicPage: React.FC = () => {
  const { token: rawToken } = useParams<{ token: string }>();
  const publicToken = decodeURIComponent(rawToken || '').trim();
  const existing = publicToken ? readStoredTodoShareSession(publicToken) : null;

  const [accessToken, setAccessToken] = useState(existing?.access_token ?? '');
  const [boardTitle, setBoardTitle] = useState(existing?.title ?? '');
  const [code, setCode] = useState('');
  const [gateError, setGateError] = useState('');
  const [verifying, setVerifying] = useState(false);

  useSharedTodoHomeScreenManifest(publicToken, boardTitle);

  useEffect(() => {
    if (!publicToken) return;
    let alive = true;
    void (async () => {
      try {
        const chrome = await loadTodoSharePublicChrome(publicToken);
        if (!alive) return;
        if (chrome.title) setBoardTitle((prev) => prev || chrome.title);
      } catch {
        /* 게이트/세션으로 이어가면 됨 */
      }
    })();
    return () => {
      alive = false;
    };
  }, [publicToken]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!publicToken) return;
    setVerifying(true);
    setGateError('');
    try {
      const session = await verifyTodoShareAccess(publicToken, code);
      setAccessToken(session.access_token);
      setBoardTitle(session.title);
    } catch (err) {
      setGateError(err instanceof Error ? err.message : '입장에 실패했습니다.');
    } finally {
      setVerifying(false);
    }
  };

  const load = useCallback(async () => {
    const snap = await loadTodoShareByAccessToken(accessToken);
    setBoardTitle(snap.title);
    return {
      title: snap.title,
      revision: snap.revision,
      categories: snap.categories,
      items: snap.items,
    };
  }, [accessToken]);

  const save = useMemo(
    () =>
      sharedEditorSaveAdapter(({ expectedRevision, payload }) =>
        saveTodoShareByAccessToken({
          accessToken,
          expectedRevision,
          payload,
        })
      ),
    [accessToken]
  );

  const topNav = <SharedTodoPublicTopNav />;

  if (!publicToken) {
    return (
      <div className="cp-todo-share-public">
        {topNav}
        <p className="cp-todo-note-error">공유 링크가 올바르지 않습니다.</p>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="cp-todo-share-public">
        {topNav}
        <form className="cp-todo-share-gate" onSubmit={(e) => void handleVerify(e)}>
          <h1>공유 ToDo 입장</h1>
          <p className="cp-todo-share-gate-desc">소유자에게 받은 입장코드를 입력해 주세요.</p>
          <label className="cp-todo-share-gate-label">
            입장코드
            <input
              type="text"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={verifying}
              autoFocus
            />
          </label>
          {gateError ? <p className="cp-todo-note-error">{gateError}</p> : null}
          <button type="submit" className="cp-btn-save-all" disabled={verifying || !code.trim()}>
            {verifying ? '확인 중…' : '입장'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="cp-todo-share-public is-editing">
      {topNav}
      <SharedTodoNoteEditor
        key={accessToken}
        load={load}
        save={save}
        allowCategoryCreate={false}
        allowRoutineCreate={false}
        onTitleChange={setBoardTitle}
        headerActions={
          <div className="cp-todo-note-scope-toggle is-board-rail cp-todo-share-exit-rail" aria-label="공유 보드 나가기">
            <button
              type="button"
              className="cp-todo-share-exit-btn"
              onClick={() => {
                clearStoredTodoShareSession(publicToken);
                setAccessToken('');
                setBoardTitle('');
              }}
            >
              퇴장
            </button>
          </div>
        }
      />
      <p className="cp-todo-share-public-foot" aria-hidden>
        {boardTitle}
      </p>
    </div>
  );
};

export default SharedTodoNotePublicPage;
